import { isAxiosError } from 'axios';
import type { AppError } from './appError';

/**
 * Normalizes any thrown value into a typed AppError.
 *
 * Mapping rules (checked in order):
 *   - AppError (passthrough)                → as-is
 *   - AxiosError with response.status 401   → unauthorized  ("Session expired")
 *   - AxiosError with response.status 403   → forbidden     ("Access denied")
 *   - AxiosError with response.status 404   → not_found     ("Not found")
 *   - AxiosError with response.status 5xx   → server_error  ("Something went wrong")
 *   - AxiosError with code ERR_NETWORK      → network_error ("Connection problem")
 *   - AxiosError (other)                    → server_error  (fallback)
 *   - Response (used by demo panel) 401–5xx → same status mapping as above
 *   - TypeError with fetch/network keywords → network_error ("Connection problem")
 *   - Any other Error / unknown             → unknown       ("Something went wrong")
 */
export function toAppError(error: unknown): AppError {
    // Passthrough for already-normalized AppErrors
    if (isAppError(error)) {
        return error;
    }

    // Axios errors — the primary source of API errors in this app
    if (isAxiosError(error)) {
        if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
            return {
                code: 'network_error',
                title: 'Connection problem',
                message: 'Please check your connection and try again.',
                cause: error,
            };
        }

        if (error.response) {
            return fromResponseStatus(error.response.status, error);
        }

        return {
            code: 'server_error',
            title: 'Something went wrong',
            message: "That didn't work. Please try again.",
            cause: error,
        };
    }

    // Plain fetch Response — used by the demo panel simulations
    if (error instanceof Response) {
        return fromResponseStatus(error.status);
    }

    if (error instanceof TypeError && isNetworkError(error)) {
        return {
            code: 'network_error',
            title: 'Connection problem',
            message: 'Please check your connection and try again.',
            cause: error,
        };
    }

    return {
        code: 'unknown',
        title: 'Something went wrong',
        message: "That didn't work. Please try again.",
        cause: error,
    };
}

function fromResponseStatus(status: number, cause?: unknown): AppError {
    if (status === 401) {
        return {
            code: 'unauthorized',
            title: 'Session expired',
            message: 'Please sign in again.',
            status,
            cause,
        };
    }

    if (status === 403) {
        return {
            code: 'forbidden',
            title: 'Access denied',
            message: 'You do not have permission to access this.',
            status,
            cause,
        };
    }

    if (status === 404) {
        return {
            code: 'not_found',
            title: 'Not found',
            message: 'We could not find what you were looking for.',
            status,
            cause,
        };
    }

    if (status >= 500 && status <= 599) {
        return {
            code: 'server_error',
            title: 'Something went wrong',
            message: "That didn't work. Please try again.",
            status,
            cause,
        };
    }

    // Unexpected HTTP status — treat as server error
    return {
        code: 'server_error',
        title: 'Something went wrong',
        message: "That didn't work. Please try again.",
        status,
        cause,
    };
}

function isNetworkError(error: TypeError): boolean {
    const msg = error.message.toLowerCase();
    return msg.includes('fetch') || msg.includes('network') || msg.includes('failed to fetch');
}

function isAppError(value: unknown): value is AppError {
    return typeof value === 'object' && value !== null && 'code' in value && 'title' in value && 'message' in value;
}
